import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Mail, Edit, Trash2, Send, Check, X, FileText, DollarSign, FileSpreadsheet } from "lucide-react";
import { SortableHead, useSortedRows } from "@/components/SortableHeader";
import { usePermissions } from "@/hooks/usePermissions";
import { QUOTE_STATUSES, quoteStatusClass } from "@/components/reports/QuotesReport";
import { downloadCsv } from "@/utils/csv";

const EMPTY = { customer_id: "", status: "", quote_number: "", date_field: "created_at", start_date: "", end_date: "" };

const ACCESSORS = {
  quote_number: (q) => q.quote_number,
  customer_name: (q) => q.customer_name,
  created_at: (q) => String(q.created_at || ""),
  valid_until: (q) => q.valid_until || "",
  status: (q) => q.status,
  total: (q) => q.total,
  created_by: (q) => q.created_by || "",
};

const money = (n) => `$${(n || 0).toFixed(2)}`;
const today = () => new Date().toISOString().slice(0, 10);
const isExpired = (q) => q.status !== "approved" && q.status !== "rejected" && q.valid_until && q.valid_until < today();

const applyFilters = (quotes, f) =>
  quotes.filter((q) => {
    if (f.customer_id && q.customer_id !== f.customer_id) return false;
    if (f.status === "expired" ? !isExpired(q) : f.status && q.status !== f.status) return false;
    if (f.quote_number && !q.quote_number.toLowerCase().includes(f.quote_number.toLowerCase())) return false;
    const v = f.date_field === "valid_until" ? q.valid_until || "" : String(q.created_at || "").slice(0, 10);
    if (f.start_date && v < f.start_date) return false;
    if (f.end_date && v > f.end_date) return false;
    return true;
  });

const Action = ({ onClick, className, title, testId, children }) => (
  <Button size="sm" variant="ghost" onClick={onClick} className={`h-8 px-2 ${className}`} title={title} data-testid={testId}>{children}</Button>
);

export const QuoteTable = ({ quotes, customers, actions, currentUser }) => {
  const { can } = usePermissions();
  const [filters, setFilters] = useState(EMPTY);
  const set = (patch) => setFilters({ ...filters, ...patch });
  const filtered = useMemo(() => applyFilters(quotes, filters), [quotes, filters]);
  const { sorted, sort, toggle } = useSortedRows(filtered, ACCESSORS, "created_at", "desc");
  const active = Object.entries(filters).some(([k, v]) => k !== "date_field" && v);
  const sum = (pred) => filtered.filter(pred).reduce((s, q) => s + (q.total || 0), 0);

  const exportCsv = () =>
    downloadCsv(
      `quotes-${today()}.csv`,
      ["Quote #", "Customer", "Quote Date", "Valid Until", "Status", "Expired", "Created By", "Subtotal", "Tax", "Total"],
      sorted.map((q) => [q.quote_number, q.customer_name, String(q.created_at || "").slice(0, 10), q.valid_until, q.status, isExpired(q) ? "yes" : "no", q.created_by || "", (q.subtotal ?? 0).toFixed(2), (q.tax ?? 0).toFixed(2), (q.total ?? 0).toFixed(2)])
    );

  const totals = [
    { label: "Filtered Total", value: money(sum(() => true)), cls: "text-slate-800", id: "quote-totals-total" },
    { label: "Approved", value: money(sum((q) => q.status === "approved")), cls: "text-green-600", id: "quote-totals-approved" },
    { label: "Pending Approval", value: money(sum((q) => q.status === "pending_approval")), cls: "text-orange-600", id: "quote-totals-pending" },
    { label: "Draft", value: money(sum((q) => q.status === "draft")), cls: "text-slate-600", id: "quote-totals-draft" },
  ];

  return (
    <div className="space-y-4">
      <Card className="bg-white border-0 shadow-lg" data-testid="quote-filters">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <div>
              <Label>Customer</Label>
              <CustomerCombobox customers={customers} value={filters.customer_id} onChange={(v) => set({ customer_id: v })} allowAll testId="quote-filter-customer" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filters.status || "all"} onValueChange={(v) => set({ status: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="quote-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {QUOTE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quote #</Label>
              <Input value={filters.quote_number} onChange={(e) => set({ quote_number: e.target.value })} placeholder="QUO-00012" data-testid="quote-filter-number" />
            </div>
            <div>
              <Label>Date Field</Label>
              <Select value={filters.date_field} onValueChange={(v) => set({ date_field: v })}>
                <SelectTrigger data-testid="quote-filter-date-field"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Quote Date</SelectItem>
                  <SelectItem value="valid_until">Valid Until</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="quote-filter-start" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid="quote-filter-end" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500" data-testid="quote-result-count">Showing {filtered.length} of {quotes.length} quotes</p>
            {active && <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY)} data-testid="quote-filters-clear"><X className="w-4 h-4 mr-1" /> Clear filters</Button>}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quote-totals-bar">
        {totals.map((t) => (
          <Card key={t.id} className="bg-white border-0 shadow"><CardContent className="p-4"><p className="text-xs uppercase tracking-wide text-slate-500">{t.label}</p><p className={`text-xl font-bold ${t.cls}`} data-testid={t.id}>{t.value}</p></CardContent></Card>
        ))}
      </div>

      <Card className="bg-white border-0 shadow-lg" data-testid="quote-table-card">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
            <p className="text-sm text-slate-500">{sorted.length} row{sorted.length === 1 ? "" : "s"} · sorted by {sort.key.replace("_", " ")} {sort.dir === "asc" ? "↑" : "↓"}</p>
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={sorted.length === 0} data-testid="quote-export-csv-button"><FileSpreadsheet className="w-4 h-4 mr-1" /> Export CSV</Button>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="quote-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <SortableHead label="Quote #" sortKey="quote_number" sort={sort} onToggle={toggle} />
                  <SortableHead label="Customer" sortKey="customer_name" sort={sort} onToggle={toggle} />
                  <SortableHead label="Quote Date" sortKey="created_at" sort={sort} onToggle={toggle} className="hidden md:table-cell" />
                  <SortableHead label="Valid Until" sortKey="valid_until" sort={sort} onToggle={toggle} />
                  <SortableHead label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                  <SortableHead label="Created By" sortKey="created_by" sort={sort} onToggle={toggle} className="hidden lg:table-cell" />
                  <SortableHead label="Total" sortKey="total" sort={sort} onToggle={toggle} className="text-right" align="right" />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="py-16 text-center" data-testid="quote-table-empty"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-600 font-medium">No quotes match the current filters</p></TableCell></TableRow>
                ) : (
                  sorted.map((quote) => (
                    <TableRow key={quote.id} className={`hover:bg-slate-50 ${actions.onView ? "cursor-pointer" : ""}`} data-testid="quote-row" onClick={() => actions.onView && actions.onView(quote)}>
                      <TableCell className="font-semibold whitespace-nowrap"><span className="text-indigo-700 hover:underline" data-testid="quote-number-link">{quote.quote_number}</span></TableCell>
                      <TableCell className="text-slate-700">{quote.customer_name}</TableCell>
                      <TableCell className="hidden md:table-cell text-slate-600 whitespace-nowrap">{String(quote.created_at || "").slice(0, 10)}</TableCell>
                      <TableCell className={`whitespace-nowrap ${isExpired(quote) ? "text-red-600 font-medium" : "text-slate-600"}`}>{quote.valid_until}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Badge className={quoteStatusClass(quote.status)} data-testid="quote-status-badge">{quote.status.replace("_", " ").toUpperCase()}</Badge>
                          {isExpired(quote) && <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="quote-expired-badge">EXPIRED</Badge>}
                          {quote.customer_decision && <Badge className={quote.customer_decision === "accepted" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-200 text-slate-700"} data-testid="quote-customer-decision-badge">CUSTOMER {quote.customer_decision.toUpperCase()}</Badge>}
                          {quote.converted_invoice_number && <Badge className="bg-blue-100 text-blue-800 border-blue-200" data-testid="quote-invoiced-badge">INVOICED {quote.converted_invoice_number}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-slate-600">{quote.created_by}{quote.approved_by && <p className="text-xs text-green-600">Approved by {quote.approved_by}</p>}</TableCell>
                      <TableCell className="text-right font-bold text-indigo-600 whitespace-nowrap">{money(quote.total)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <Action onClick={() => actions.downloadPDF(quote.id, quote.quote_number)} className="text-green-600 hover:bg-green-50" title="Download PDF" testId="download-quote-pdf-button"><Download className="w-4 h-4" /></Action>
                        {can("quotes.send") && <Action onClick={() => actions.emailQuote(quote.id)} className="text-orange-600 hover:bg-orange-50" title="Email quote" testId="email-quote-button"><Mail className="w-4 h-4" /></Action>}
                        {quote.status === "draft" && can("quotes.edit") && (
                          <>
                            <Action onClick={() => actions.onEdit(quote)} className="text-blue-600 hover:bg-blue-50" title="Edit" testId="edit-quote-button"><Edit className="w-4 h-4" /></Action>
                            <Action onClick={() => actions.submitForApproval(quote.id)} className="text-indigo-600 hover:bg-indigo-50" title="Submit for approval" testId="submit-quote-button"><Send className="w-4 h-4" /></Action>
                          </>
                        )}
                        {quote.status === "pending_approval" && can("quotes.approve") && (
                          <>
                            <Action onClick={() => actions.approve(quote.id)} className="text-green-700 hover:bg-green-50" title="Approve" testId="approve-quote-button"><Check className="w-4 h-4" /></Action>
                            <Action onClick={() => actions.reject(quote.id)} className="text-red-600 hover:bg-red-50" title="Reject" testId="reject-quote-button"><X className="w-4 h-4" /></Action>
                          </>
                        )}
                        {quote.status === "approved" && !quote.converted_invoice_number && can("quotes.convert") && (
                          <Action onClick={() => actions.convertToInvoice(quote.id)} className="text-purple-600 hover:bg-purple-50" title="Convert to invoice" testId="convert-quote-button"><DollarSign className="w-4 h-4" /></Action>
                        )}
                        {can("quotes.delete") && <Action onClick={() => actions.onDelete(quote.id)} className="text-red-600 hover:bg-red-50" title="Delete" testId="delete-quote-button"><Trash2 className="w-4 h-4" /></Action>}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
